import * as Phaser from "phaser"
import { InputManager } from "../core/input-mapper"
import { AUDIO, SCENE, TEX } from "../core/keys"
import { setMusicMenuVolume, startMusicSequence } from "../core/music-volume"
import { PersistedData } from "../core/persisted-data"
import { THEME } from "../core/theme"
import { playClick, playHover } from "../core/ui-sfx"
import { AUDIO_ASSETS } from "../data/audio"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import { RetroButton } from "../ui/components"
import { applyVignetteFilter, drawScanlines } from "../ui/neon-fx"
import { toPublicAssetUrl } from "../utils"

const FONT = THEME.fonts

interface Star {
  x: number
  y: number
  speed: number
  color: number
  alpha: number
  size: number
}

export class MenuScene extends Phaser.Scene {
  private starLayers: {
    graphics: Phaser.GameObjects.Graphics
    stars: Star[]
  }[] = []
  private inputManager!: InputManager
  private selectedIndex = 0
  private menuButtons: RetroButton[] = []
  private logoContainer!: Phaser.GameObjects.Container
  private planet!: Phaser.GameObjects.Image
  private pressStartText!: Phaser.GameObjects.Text
  private isTitleActive = true

  constructor() {
    super({ key: SCENE.MENU })
  }

  create(): void {
    const cx = GAME_WIDTH / 2
    const cy = GAME_HEIGHT / 2

    this.selectedIndex = 0
    this.menuButtons = []
    this.starLayers = []
    this.isTitleActive = false // Skip title phase

    if (this.sound.get(AUDIO.MUSIC_01)) {
      setMusicMenuVolume(this)
    } else if (this.cache.audio.exists(AUDIO.MUSIC_01)) {
      startMusicSequence(this)
    } else {
      this.loadMenuMusic()
    }

    this.cameras.main.fadeIn(1000, 0, 0, 0)

    // 1. Background Layers
    this.renderSpaceBackground()
    this.createStarfield()

    // 2. Input & UI
    this.setupInput()

    this.renderSnesLogo(cx, cy - 80)
    // Press start text is created but hidden
    this.renderPressStart(cx, cy + 60)
    this.pressStartText.setVisible(false)

    // Menu buttons are created and shown immediately
    this.createMenuOptions(cx, cy + 20)
    this.menuButtons.forEach((btn) => {
      btn.setVisible(true)
      btn.setAlpha(1)
    })
    this.updateSelection()

    this.renderFooter(cx, GAME_HEIGHT - 20)

    // 3. Post FX
    drawScanlines(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, 0.05, 3).setDepth(100)
    applyVignetteFilter(this.cameras.main, { strength: 0.4, radius: 0.4 })

    this.events.once("shutdown", () => {
      this.inputManager.destroy()
    })
  }

  update(_time: number, delta: number): void {
    // Update starfield parallax
    for (const layer of this.starLayers) {
      layer.graphics.clear()
      for (const star of layer.stars) {
        star.y += star.speed * (delta / 16.6)
        if (star.y > GAME_HEIGHT) {
          star.y = -10
          star.x = Phaser.Math.Between(0, GAME_WIDTH)
        }
        layer.graphics.fillStyle(star.color, star.alpha)
        layer.graphics.fillRect(star.x, star.y, star.size, star.size)
      }
    }

    // Gentle planet float
    if (this.planet) {
      this.planet.y += Math.sin(_time * 0.001) * 0.05
    }
  }

  private renderSpaceBackground(): void {
    const bg = this.add.graphics()
    // Deep space gradient
    bg.fillGradientStyle(0x02040a, 0x02040a, 0x050a1f, 0x050a1f, 1)
    bg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT)
  }

  private createStarfield(): void {
    const configs = [
      { count: 40, speed: 0.2, size: 1, alpha: 0.3, color: 0xffffff },
      { count: 25, speed: 0.5, size: 1, alpha: 0.6, color: 0xaaaaff },
      { count: 15, speed: 1.2, size: 2, alpha: 0.8, color: 0xffffff },
    ]

    for (const config of configs) {
      const graphics = this.add.graphics().setDepth(-10)
      const stars: Star[] = []
      for (let i = 0; i < config.count; i++) {
        stars.push({
          x: Phaser.Math.Between(0, GAME_WIDTH),
          y: Phaser.Math.Between(0, GAME_HEIGHT),
          speed: config.speed,
          size: config.size,
          alpha: config.alpha,
          color: config.color,
        })
      }
      this.starLayers.push({ graphics, stars })
    }
  }

  private loadMenuMusic(): void {
    const music = AUDIO_ASSETS.find((asset) => asset.key === AUDIO.MUSIC_01)
    if (!music) return

    this.load.audio(music.key, toPublicAssetUrl(music.assetPath))
    this.load.once("complete", () => {
      if (this.scene.isActive(SCENE.MENU)) {
        startMusicSequence(this)
      }
    })
    this.load.start()
  }

  private renderSnesLogo(cx: number, y: number): void {
    this.logoContainer = this.add.container(cx, y)

    const title = "BARK TO THE FUTURE"
    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: FONT.sans,
      fontSize: "32px",
      fontStyle: "900 italic",
      color: "#ffffff",
      stroke: "#000000",
      strokeThickness: 8,
    }

    const shadow = this.add
      .text(4, 4, title, { ...style, color: "#111122" })
      .setOrigin(0.5)
    const mid = this.add.text(0, 0, title, style).setOrigin(0.5)
    const top = this.add
      .text(0, -2, title, { ...style, strokeThickness: 0, color: "#44ccff" })
      .setOrigin(0.5)
    const shine = this.add
      .text(0, -2, title, { ...style, strokeThickness: 0, color: "#ffffff" })
      .setOrigin(0.5)
    shine.setAlpha(0)

    this.logoContainer.add([shadow, mid, top, shine])

    this.logoContainer.setScale(0)
    this.tweens.add({
      targets: this.logoContainer,
      scale: 1,
      duration: 800,
      ease: "Back.easeOut",
      onComplete: () => {
        this.time.addEvent({
          delay: 3000,
          loop: true,
          callback: () => {
            shine.setAlpha(1)
            shine.x = -20
            this.tweens.add({
              targets: shine,
              x: 20,
              alpha: 0,
              duration: 500,
              ease: "Linear",
            })
          },
        })
      },
    })

    const sub = this.add
      .text(0, 35, "DOGS x MACHINES", {
        fontFamily: FONT.pixel,
        fontSize: "10px",
        color: "#ffd700",
        letterSpacing: 4,
      })
      .setOrigin(0.5)
      .setAlpha(0)
    this.logoContainer.add(sub)
    this.tweens.add({ targets: sub, alpha: 1, delay: 1000, duration: 500 })
  }

  private renderPressStart(cx: number, y: number): void {
    const confirmLabel = InputManager.getLabelForAction("CONFIRM")
    this.pressStartText = this.add
      .text(cx, y, `PRESS ${confirmLabel} / START`, {
        fontFamily: FONT.pixel,
        fontSize: "12px",
        color: "#ffffff",
        stroke: "#000000",
        strokeThickness: 2,
      })
      .setOrigin(0.5)

    this.tweens.add({
      targets: this.pressStartText,
      alpha: 0,
      duration: 1,
      hold: 500,
      repeatDelay: 500,
      repeat: -1,
      yoyo: true,
    })
  }

  private createMenuOptions(cx: number, startY: number): void {
    const options = [
      { text: "NEW GAME", action: () => this.startNewGame() },
      {
        text: "OPTIONS",
        action: () => this.scene.start(SCENE.OPTIONS, { returnTo: SCENE.MENU }),
      },
      {
        text: "TUTORIAL",
        action: () =>
          this.scene.start(SCENE.TUTORIAL, { returnTo: SCENE.MENU }),
      },
    ]

    const btnW = 120
    const btnH = 34
    const spacing = 42

    options.forEach((opt, i) => {
      const btn = new RetroButton(this, {
        inputManager: this.inputManager,
        x: cx - btnW / 2,
        y: startY + i * spacing,
        width: btnW,
        height: btnH,
        text: opt.text,
        color: 0x223366,
        isFilled: true,
        onClick: opt.action,
      })

      btn.setAlpha(0)
      btn.setVisible(false)
      this.menuButtons.push(btn)
    })
  }

  private showMenu(): void {
    if (!this.isTitleActive) return
    this.isTitleActive = false

    playClick(this)
    this.pressStartText.setVisible(false)

    this.menuButtons.forEach((btn, i) => {
      btn.setVisible(true)
      btn.y += 20
      this.tweens.add({
        targets: btn,
        alpha: 1,
        y: btn.y - 20,
        delay: i * 100,
        duration: 300,
        ease: "Back.easeOut",
      })
    })

    this.updateSelection()
  }

  private renderFooter(cx: number, y: number): void {
    this.add
      .text(cx, y, "© 19TH STREET STUDIO 2026", {
        fontFamily: FONT.pixel,
        fontSize: "8px",
        color: "#445588",
      })
      .setOrigin(0.5)
  }

  private setupInput(): void {
    this.inputManager = new InputManager(this, {
      onDown: (action, event) => {
        if (this.isTitleActive) {
          if (action === "CONFIRM" || action === "FLAP" || action === "PAUSE") {
            // Only allow non-pointer events to trigger the menu from title,
            // OR clicks if they are actually desired (usually yes for "Press Start")
            // But if the user says "clicking any location and it goes to new game",
            // maybe they are clicking while it's still transitioning?
            this.showMenu()
          }
          return
        }

        if (action === "UP") {
          this.selectedIndex =
            (this.selectedIndex - 1 + this.menuButtons.length) %
            this.menuButtons.length
          playHover(this)
          this.updateSelection()
        } else if (action === "DOWN") {
          this.selectedIndex =
            (this.selectedIndex + 1) % this.menuButtons.length
          playHover(this)
          this.updateSelection()
        } else if (action === "CONFIRM" || action === "FLAP") {
          // Ignore global pointer events for these actions in the menu,
          // as we only want to respond to keyboard/gamepad or direct button clicks.
          if (event?.pointer) return

          playClick(this)
          this.triggerSelectedAction()
        }
      },
    })
  }

  private updateSelection(): void {
    this.menuButtons.forEach((btn, i) => {
      btn.setSelected(i === this.selectedIndex)
    })
  }

  private triggerSelectedAction(): void {
    if (this.selectedIndex === 0) this.startNewGame()
    else if (this.selectedIndex === 1)
      this.scene.start(SCENE.OPTIONS, { returnTo: SCENE.MENU })
    else if (this.selectedIndex === 2)
      this.scene.start(SCENE.TUTORIAL, { returnTo: SCENE.MENU })
  }

  private startNewGame(): void {
    this.time.delayedCall(200, () => {
      this.scene.start(SCENE.CHARACTER_SELECT)
    })
  }
}
